/**
 * WEEK chips (W1…Wn) colored by status, plus the 完了 / 着手中 / 未着手 legend below them.
 */

import { FC, memo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { black, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { TSubjectCurriculumDto, TSubjectStatusDto } from "@/api/services/main/curriculum-progress/dtos/get-curriculum-progress.dto";
import { TCurriculumGroupCounts, getWeekLabel } from "@/features/dashboard/utils/curriculumGroups";
import { WEEK_STATUS_COLORS } from "@/features/dashboard/components/learning-progress/CurriculumProgressBar";

type TWeekChipItem = Pick<TSubjectCurriculumDto, "id" | "name" | "status">;

type TWeekStatusChipsProps = {
  subjects: TWeekChipItem[];
  counts: TCurriculumGroupCounts;
};

/** Chip fill + label color per status — 未着手 keeps a light chip with dark text */
const CHIP_STYLES: Record<TSubjectStatusDto, { bgcolor: string; color: string }> = {
  completed: { bgcolor: WEEK_STATUS_COLORS.completed, color: text.white },
  in_progress: { bgcolor: WEEK_STATUS_COLORS.inProgress, color: text.white },
  not_started: { bgcolor: WEEK_STATUS_COLORS.notStarted, color: black[30] },
};

const LEGEND_DOT_SIZE = 12;

/** Chips per row — keeps W1…Wn aligned in a grid instead of stretching to fill each row */
const CHIPS_PER_ROW = 5;

export const WeekStatusChips: FC<TWeekStatusChipsProps> = memo(({ subjects, counts }) => {
  const { t } = useTranslation();

  return (
    <Stack gap={1}>
      {/* Chip grid — fixed columns so every wrapped row keeps the same chip width */}
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${CHIPS_PER_ROW}, 1fr)`, gap: 0.5 }}>
        {subjects.map((subject, index) => (
          <Box
            key={subject.id}
            sx={{
              height: "21px",
              borderRadius: rounded.xs,
              textAlign: "center",
              ...CHIP_STYLES[subject.status],
            }}
          >
            <Typography sx={{ ...typography.body14Bold, color: "inherit" }}>{getWeekLabel(subject, index)}</Typography>
          </Box>
        ))}
      </Box>

      {/* Legend */}
      <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.5}>
        <LegendItem color={WEEK_STATUS_COLORS.completed} label={`${t("dashboard.learningProgress.completed")}: ${counts.completed}`} />
        <LegendItem color={WEEK_STATUS_COLORS.inProgress} label={`${t("dashboard.learningProgress.inProgress")}: ${counts.inProgress}`} />
        <LegendItem color={WEEK_STATUS_COLORS.notStarted} label={`${t("dashboard.learningProgress.notStarted")}: ${counts.notStarted}`} />
      </Stack>
    </Stack>
  );
});
WeekStatusChips.displayName = "WeekStatusChips";

/** Colored square + caption used under the chips */
const LegendItem: FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Box sx={{ width: LEGEND_DOT_SIZE, height: LEGEND_DOT_SIZE, borderRadius: rounded.xs, bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ ...typography.body14, color: text.secondary, whiteSpace: "nowrap" }}>{label}</Typography>
  </Stack>
);
